import { createAuthHeaders, createJsonPostHeaders } from '../utils/httpUtils';

const API_PREFIX = "/api"

function hasValue(value) {
    return value !== undefined && value !== null && value !== "";
}

export function getBoostValues(requestConfig) {
    return requestConfig.selectedFields.map(
        (field) => requestConfig.fieldBoosts[field] ?? 1
    );
}

export function transformFilters(filters) {
    return filters
        .filter((filter) => filter.key && hasValue(filter.value))
        .map((filter) => ({
            [filter.key]: filter.value,
        }));
}

export async function fetchKeywordWeight(config) {
    const res = await fetch(`${API_PREFIX}/index/hybrid/weights`, {
        method: "GET",
        headers: createAuthHeaders(config),
    });

    if (!res.ok) {
        throw new Error(`Failed to load keyword_weight: ${res.status}`);
    }

    const data = await res.json();

    return data?.keyword_weight ?? null;
}

export async function updateKeywordWeight(config, weight) {
    const res = await fetch(`${API_PREFIX}/index/hybrid/weights?keyword_weight=${weight}`, {
        method: 'POST',
        headers: createAuthHeaders(config),
        body: '',
    })

    if (!res.ok) {
        throw new Error(`Failed to update keyword_weight: ${res.status}`);
    }
}

export async function searchAugment(config, requestConfig, fromPosition, searchContext, selectedFilters = []) {
    const mergedFilters = [...(requestConfig.filters || []), ...selectedFilters];
    const reqBody = JSON.stringify({
        "query": searchContext.queryOverride,
        "threshold": (requestConfig.sliderValue * 0.0001),
        "max_results": requestConfig.maxResults,
        "from_pos": fromPosition,
        "search_fields": requestConfig.selectedFields,
        "field_boosts": getBoostValues(requestConfig),
        "filter_key_value": transformFilters(mergedFilters),
        "not_filter_key_value": transformFilters(requestConfig.notFilters),
        "correct_query": searchContext.correctQuery,
        "requested_sort_order": "detect",
    })

    const res = await fetch(`${API_PREFIX}/search/hybrid`, {
        method: 'POST',
        headers: createJsonPostHeaders(config),
        body: reqBody,
    })

    if (!res.ok) {
        throw new Error('Failed to perform augment search');
    }

    const data = await res.json();
    return data
}

export async function searchFilterCategorizer(config, queryText) {
    const model = config.CATEGORIZER_MODEL;
    const endpoint = `${API_PREFIX}/query/classify-filter?mode=${model}`;
    const res = await fetch(endpoint, {
        method: 'POST',
        headers: createJsonPostHeaders(config),
        body: JSON.stringify({
            query: queryText,
            top_k: 10,
            max_results: 10,
            from_pos: 0,
        }),
    })

    if (!res.ok) {
        throw new Error(await res.text());
    }

    const data = await res.json();
    return data
}

export async function getLookupSuggestions(config, queryText) {
    const endpoint = `${API_PREFIX}/suggestions/curated`;
    const res = await fetch(endpoint, {
        method: 'POST',
        headers: createJsonPostHeaders(config),
        body: JSON.stringify({
            query: queryText,
            top_k: 3,
            max_results: 10,
            from_pos: 0,
        }),
    })

    if (!res.ok) {
        throw new Error(await res.text());
    }

    const data = await res.json();
    return data.curated_suggestions
}

export async function searchIntent(config, body) {
    const res = await fetch(`${API_PREFIX}/suggestions/related`, {
        method: 'POST',
        headers: createJsonPostHeaders(config),
        body: JSON.stringify({
            query: body.queryOverride
        }),
    })

    if (!res.ok) {
        throw new Error(await res.text());
    }

    const data = await res.json();
    return data.related_suggestions
}

export async function trendingQueries(config) {
    const res = await fetch(`${API_PREFIX}/suggestions/popular`, {
        method: 'GET',
        headers: createAuthHeaders(config),
    })

    if (!res.ok) {
        throw new Error(await res.text());
    }

    const data = await res.json();
    return data
}

export async function searchFreeText(config, requestConfig, fromPosition, searchContext) {
    const res = await fetch(`${API_PREFIX}/search/filter`, {
        method: 'POST',
        headers: createJsonPostHeaders(config),
        body: JSON.stringify({
            query: searchContext.query,
            max_results: requestConfig.maxResults,
            from_pos: fromPosition,
        }),
    })

    if (!res.ok) {
        throw new Error(await res.text());
    }

    const data = await res.json();
    return data
}

export async function searchAgentic(config, requestConfig, fromPosition, searchContext) {
    const res = await fetch(`${API_PREFIX}/search/agentic`, {
        method: 'POST',
        headers: createJsonPostHeaders(config),
        body: JSON.stringify({
            query: searchContext.query,
        }),
    })

    if (!res.ok) {
        throw new Error(await res.text());
    }

    const data = await res.json();
    return data
}

export async function searchArdOnline(config, requestConfig, fromPosition, searchContext) {
    const pageSize = requestConfig.maxResults;
    const pageNumber = Math.floor(fromPosition / pageSize);

    const res = await fetch(`${API_PREFIX}/search/proxy`, {
        method: 'POST',
        headers: createJsonPostHeaders(config),
        body: JSON.stringify({
            query: searchContext.query,
            page_number: pageNumber,
            page_size: pageSize,
        }),
    })

    if (!res.ok) {
        throw new Error(await res.text());
    }

    const data = await res.json();
    return data
}

export async function searchConversational(config, requestConfig, fromPosition, searchContext) {
    const body = { ...searchContext.body, max_results: requestConfig.maxResults, from_pos: fromPosition }

    const res = await fetch(`${API_PREFIX}/discovery/funnel`, {
        method: 'POST',
        headers: createJsonPostHeaders(config),
        body: JSON.stringify(body),
    })

    if (!res.ok) {
        throw new Error(await res.text());
    }

    const data = await res.json();
    return data
}

export async function searchPopularItems(config, requestConfig, fromPosition, searchContext) {
    const res = await fetch(`${API_PREFIX}/discovery/items/related`, {
        method: 'POST',
        headers: createJsonPostHeaders(config),
        body: JSON.stringify({
            ids: searchContext.ids,
            max_results: requestConfig.maxResults,
            from_pos: fromPosition,
        }),
    })

    if (!res.ok) {
        throw new Error(await res.text());
    }

    const data = await res.json();
    return data
}

export async function getPopularItems(config) {
    const res = await fetch(`${API_PREFIX}/discovery/items/popular`, {
        method: "GET",
        headers: createAuthHeaders(config),
    });

    if (!res.ok) {
        throw new Error(`Failed to get popular items: ${res.status}`);
    }

    const data = await res.json();
    return data;
}

export async function transcribeAudio(config, formData) {
    const res = await fetch(`${API_PREFIX}/audio/transcribe`, {
        method: 'POST',
        headers: createAuthHeaders(config),
        body: formData,
    })

    if (!res.ok) {
        const errText = await res.text();
        throw new Error(errText || 'Error in transcription');
    }
    const data = await res.json();
    return data
}

export async function textToSpeech(config, body) {
    const res = await fetch(`${API_PREFIX}/audio/generate`, {
        method: 'POST',
        headers: createJsonPostHeaders(config),
        body: JSON.stringify(body),
    })

    if (!res.ok) {
        throw new Error(await res.text());
    }

    if (!res.body) {
        throw new Error("No response body (streaming not supported).");
    }

    return res
}